import type { FC } from 'react';
import { useContext } from 'react';
import { useSelector } from 'react-redux';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import ErrorBoundary from '@wix/da-react-context/pkg/ErrorBoundary';
import LoadingIndicator from '@wix/da-ds/pkg/LoadingIndicator';
import { getIsDuperbrowseActive } from '@wix/da-shared-react/pkg/Duperbrowse/redux/selectors';
import FoldersHeader from '../FoldersHeader';
import FolderSlider from '../FolderSlider';
import GallectionDeviationsTorpedo from '../GallectionDeviationsTorpedo';
import SearchResultsBar from './SearchResultsBar';
import SearchEmptyState from './SearchEmptyState';
import MobileGallectionHeader from '../MobileGallectionHeader';
import InfiniteScrollControls from '../../_partials/StreamPagination/InfiniteScrollControls';
import PagedPagination from '../../_partials/StreamPagination/PagedPagination';
import useInfiniteGallectionSearch from './hooks/useInfiniteGallectionSearch';
import usePagedGallectionSearch from './hooks/usePagedGallectionSearch';
import { getCurrentPage } from '../../../../selectors/pageInfo';
import { getIsPagedMode } from '../../../../selectors/users';

import s from './GallectionSearchView.scss';

export interface Props {}

interface SearchViewChromeProps {
  hideFolderSlider: boolean;
  isSearching: boolean;
  resultsTotal: number;
  searchQuery: string;
}

const SearchViewChrome: FC<SearchViewChromeProps> = ({
  hideFolderSlider,
  isSearching,
  resultsTotal,
  searchQuery,
}) => {
  const isMobile = useContext(MobileContext);

  return (
    <>
      {!isMobile && <FoldersHeader />}
      {!isMobile && !hideFolderSlider && <FolderSlider />}
      {isMobile ? (
        <MobileGallectionHeader searchQuery={searchQuery} resultsTotal={resultsTotal} />
      ) : (
        <SearchResultsBar
          searchQuery={searchQuery}
          resultsTotal={resultsTotal}
          isSearching={isSearching}
        />
      )}
    </>
  );
};

SearchViewChrome.displayName = 'SearchViewChrome';

const InfiniteGallectionSearchView: FC = () => {
  const isDuperbrowseActive = useSelector(state => getIsDuperbrowseActive(state as any));
  const {
    currentPage,
    deviations,
    errorMessage,
    gallectionType,
    hideFolderSlider,
    isLoadingFirstResults,
    requestUrl,
    searchQuery,
    streamHasMore,
    streamId,
    streamTotalCount,
  } = useInfiniteGallectionSearch();
  const isEmpty = !isLoadingFirstResults && !deviations.length && !streamHasMore;

  return (
    <>
      <SearchViewChrome
        hideFolderSlider={hideFolderSlider}
        isSearching={isLoadingFirstResults}
        resultsTotal={streamTotalCount}
        searchQuery={searchQuery}
      />
      {deviations.length > 0 && (
        <>
          <div>
            <GallectionDeviationsTorpedo
              streamId={streamId}
              gallectionType={gallectionType}
              deviations={deviations}
            />
          </div>
          <InfiniteScrollControls
            currentPage={currentPage}
            error={errorMessage}
            isDuperbrowseActive={isDuperbrowseActive}
            requestUrl={requestUrl}
            streamId={streamId}
          />
        </>
      )}
      {isEmpty && <SearchEmptyState />}
      {isLoadingFirstResults && <LoadingIndicator />}
    </>
  );
};

InfiniteGallectionSearchView.displayName = 'InfiniteGallectionSearchView';

const PagedGallectionSearchView: FC = () => {
  const {
    currentPage,
    deviations,
    gallectionType,
    hideFolderSlider,
    isLoadingFirstResults,
    requestUrl,
    searchQuery,
    streamHasMore,
    streamId,
    streamTotalCount,
    totalPages,
  } = usePagedGallectionSearch();
  const isEmpty = !isLoadingFirstResults && !deviations.length && !streamHasMore;

  return (
    <>
      <SearchViewChrome
        hideFolderSlider={hideFolderSlider}
        isSearching={isLoadingFirstResults}
        resultsTotal={streamTotalCount}
        searchQuery={searchQuery}
      />
      {deviations.length > 0 && (
        <>
          <div>
            <GallectionDeviationsTorpedo
              streamId={streamId}
              gallectionType={gallectionType}
              deviations={deviations}
            />
          </div>
          <PagedPagination
            className={s['pagination-wrapper']}
            currentPage={currentPage}
            requestUrl={requestUrl}
            showPageNumbers={false}
            totalPages={totalPages}
          />
        </>
      )}
      {isEmpty && <SearchEmptyState />}
      {isLoadingFirstResults && <LoadingIndicator />}
    </>
  );
};

PagedGallectionSearchView.displayName = 'PagedGallectionSearchView';

export const GallectionSearchView: FC<Props> = () => {
  const currentPage = useSelector(getCurrentPage);
  const isPagedMode = useSelector(getIsPagedMode);
  const isPagedView = isPagedMode || currentPage > 1;

  return (
    <ErrorBoundary debugComponent="GallectionSearchView">
      {isPagedView ? <PagedGallectionSearchView /> : <InfiniteGallectionSearchView />}
    </ErrorBoundary>
  );
};

GallectionSearchView.displayName = 'GallectionSearchView';

export default GallectionSearchView;
